import { useMemo, useRef, type ReactNode } from 'react'
import {
  ButtonDelete,
  ButtonEdit,
  ButtonExternal,
  ConversationAudioPlayer,
  ConversationTranscript,
  NotReady,
  OptionalRow,
  Page,
  UserInfo,
} from '@components'
import {
  useCompliancePolicy,
  useComplianceViolation,
  useConversation,
  useRemoveComplianceViolation,
  useRemoveConversation,
} from '@front/model'
import { useToastError } from '@hooks'
import { Grid, Group, Modal, Pill, type PillProps, Stack, Text, Title } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { useNavigate, useRouter, createFileRoute } from '@tanstack/react-router'
import {
  type CompliancePolicy,
  type ComplianceViolation,
  type WithComplianceViolationId,
  type WithUserId,
} from '@terros/common'
import { useRequiredParams } from '@util'
import { DateTime } from 'luxon'
import { TimestampPills } from './TimestampPill'

function ViewComplianceViolation(): ReactNode {
  const { violationId } = useRequiredParams<WithComplianceViolationId>()
  const { data, error, isLoading, refetch, isFetching } = useComplianceViolation(violationId)
  const navigate = useNavigate()
  const router = useRouter()
  const {
    remove: removeViolationDb,
    isRemoving: isRemovingViolation,
    error: removeViolationError,
  } = useRemoveComplianceViolation()
  const {
    remove: removeConversationDb,
    isRemoving: isRemovingConversation,
    error: removeConversationError,
  } = useRemoveConversation()
  useToastError(removeViolationError)
  useToastError(removeConversationError)

  const [opened, { open, close }] = useDisclosure(false)

  const isRemoving = isRemovingViolation || isRemovingConversation

  if (!data) return <NotReady data={data} error={error} id='Violation' isLoading={isLoading} />

  const removeViolation = async (): Promise<void> => {
    await removeViolationDb({ violationId })
    notifications.show({ message: 'Violation Removed', color: 'green' })
    router.history.back()
  }

  const removeConversation = async (): Promise<void> => {
    await removeConversationDb({ conversationId: data.convoId })
    notifications.show({ message: 'Conversation Removed', color: 'green' })
    refetch()
  }

  const removeBoth = async (): Promise<void> => {
    await Promise.allSettled([removeConversation(), removeViolation()])
  }

  return (
    <Page>
      <Page.Heading title='View Violation' back goBackBehavior='history' refetch={refetch} isFetching={isFetching}>
        <ButtonDelete onClick={() => open()} loading={isRemoving} />
      </Page.Heading>
      <Page.Content scrollArea>
        {data && <ConversationViolation violation={data} />}
        <Modal opened={opened} onClose={close} title='Delete violation?' centered>
          <Stack>
            <Group grow>
              <ButtonDelete onClick={() => removeViolation()}>Delete Violation</ButtonDelete>
              <ButtonDelete onClick={() => removeConversation()}>Delete Conversation</ButtonDelete>
            </Group>
            <ButtonDelete onClick={() => removeBoth()}>Delete Both</ButtonDelete>
          </Stack>
        </Modal>
      </Page.Content>
    </Page>
  )
}

const ConversationViolation = ({ violation }: { violation: ComplianceViolation }): ReactNode => {
  const { userId, convoId, policyId } = violation

  const { data: convo, isFetching: isConvoFetching, error: convoError } = useConversation(convoId)
  const { data: policy, isFetching: isPolicyFetching, error: policyError } = useCompliancePolicy(policyId)

  const isFetching = isConvoFetching || isPolicyFetching

  const audioRef = useRef<HTMLAudioElement>(null)

  const setAudioPosition = (position: number): void => {
    if (audioRef.current) {
      audioRef.current.currentTime = position
    }
  }

  const formattedDate = useMemo(() => {
    return DateTime.fromMillis(violation.violationDate).toLocaleString(DateTime.DATETIME_FULL)
  }, [violation.violationDate])

  const formattedExpiresAt = useMemo(() => {
    if (!violation.expiresAt) return
    return DateTime.fromSeconds(violation.expiresAt).toLocaleString(DateTime.DATETIME_FULL)
  }, [violation.expiresAt])

  if (isFetching) return <NotReady error={convoError ?? policyError} isLoading={isFetching} />

  return (
    <Grid>
      <Grid.Col span={{ base: 12, lg: 6, xl: 6 }}>
        <Stack>
          <Title order={3}>Violation Details</Title>
          {convo && <ConversationAudioPlayer conversation={convo} audioRef={audioRef} />}
          <Group justify='center'>
            <TimestampPills timestamps={violation.timestamps} audioRef={audioRef} />
          </Group>
          <OptionalRow label='Policy' value={<PolicyRow policy={policy} />} />
          <OptionalRow label='User' value={<UserRow userId={userId} />} />
          <OptionalRow label='Conversation Time' value={formattedDate} canCopy />
          <OptionalRow label='Expires at' value={formattedExpiresAt} canCopy />
          <OptionalRow label='Nearest Location ID' value={convo?.locationId} canCopy />
          <OptionalRow
            label='Conversation ID'
            value={
              convoError ? (
                <>
                  <ErrorPill title={convoError} />
                  {convoId}
                </>
              ) : (
                convoId
              )
            }
            canCopy
          />
          <OptionalRow
            label='Policy ID'
            value={
              policyError ? (
                <>
                  <ErrorPill title={policyError} />
                  {policyId}
                </>
              ) : (
                policyId
              )
            }
            canCopy
          />
          <OptionalRow label='User ID' value={violation.userId} canCopy />
        </Stack>
      </Grid.Col>
      <Grid.Col span={{ base: 12, lg: 6, xl: 6 }}>
        <Stack>
          <Title order={3}>Conversation Transcription</Title>
          {convo ? (
            <ConversationTranscript conversation={convo} onSeek={setAudioPosition} />
          ) : (
            <Text>No transcription available</Text>
          )}
        </Stack>
      </Grid.Col>
    </Grid>
  )
}

const ErrorPill = (props: PillProps): ReactNode => (
  <Pill {...props} bg='red'>
    Error
  </Pill>
)

const PolicyRow = ({ policy }: { policy?: CompliancePolicy }): ReactNode => {
  if (!policy) return null
  return (
    <>
      <ButtonEdit variant='ghost' buttonType='icon-rounded' /> {policy?.title}
    </>
  )
}

const UserRow = ({ userId }: WithUserId): ReactNode => {
  return (
    <Group>
      <ButtonExternal to={`/compliance/violations/${userId}`}>View all violations</ButtonExternal>
      <UserInfo userId={userId} />
    </Group>
  )
}

export const Route = createFileRoute('/compliance_/violation_/$violationId/')({
  component: ViewComplianceViolation,
})
